import { useCallback, useState } from 'react';

import { noop } from '@adsk/alloy-react-helpers';

import { removeOldDuplicatesBy } from '../helpers/removeOldDuplicatesBy';

/**
 * This hook aggregates data from client side, and provides `modifyData` method to reflect those changes on BE data.
 *
 * @param onDataChange
 * @returns {{modifyData: function, addData: function, removeEntityById: function, updateEntity: function}}
 */
export default <T extends { id: string }>(onDataChange = noop) => {
  const [removedIds, setRemovedIds] = useState<string[]>([]);
  const [updatedEntitiesMap, setUpdatedEntitiesMap] = useState<
    Record<string, T>
  >({});
  const [addedEntities, setAddedEntities] = useState<T[]>([]);

  const modifyData = useCallback(
    (data: T[]) => {
      const handleAdd = (d: T[]) =>
        addedEntities.length
          ? removeOldDuplicatesBy(
              [...d, ...addedEntities],
              (item: T) => item.id,
            )
          : d;
      const handleUpdate = (d: T[]) =>
        Object.keys(updatedEntitiesMap).length
          ? d.map((entity) => updatedEntitiesMap[entity.id] || entity)
          : d;
      const handleRemove = (d: T[]) =>
        removedIds.length
          ? d.filter((entity) => !removedIds.includes(entity.id))
          : d;
      return handleUpdate(handleRemove(handleAdd(data)));
    },
    [removedIds, updatedEntitiesMap, addedEntities],
  );

  const addData = useCallback(
    (newData: T[]) => {
      onDataChange();
      // in next line we are checking if our new user is already in the removeIds.
      // it solves case that the user is re-added after we deleted him.
      setRemovedIds((prevRemoveIds) =>
        prevRemoveIds.length
          ? prevRemoveIds.filter(
              (id) => !newData.some((item: T) => item.id === id),
            )
          : prevRemoveIds,
      );
      setAddedEntities((prevAddedEntities) => [
        ...prevAddedEntities,
        ...newData,
      ]);
    },
    [onDataChange],
  );

  const updateEntity = useCallback(
    (entity: T) => {
      onDataChange();
      setUpdatedEntitiesMap((prevUpdatedEntitiesMap) => ({
        ...prevUpdatedEntitiesMap,
        [entity.id]: entity,
      }));
    },
    [onDataChange],
  );

  const removeEntityById = useCallback(
    (id: string) => {
      onDataChange();
      setRemovedIds((prevIds) => [...prevIds, id]);
    },
    [onDataChange],
  );

  return {
    modifyData,
    addData,
    updateEntity,
    removeEntityById,
  };
};
